// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductCarouselProduct, { ProductCarouselProductStyles } from "@bg/Overrides/Components/ProductCarouselProduct";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { styledSlider, xScreen } from "@bg/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getProductIdsForCompare,
    getProductsForCompare,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import removeProductIdToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { ReactNode, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
// eslint-disable-next-line
import SwiperCore, { A11y, Navigation, Pagination, Scrollbar } from "swiper";
// eslint-disable-next-line
import { Swiper, SwiperSlide } from "swiper/react";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y]);

interface OwnProps {
    id: string;
    extendedStyles?: CarouselStyles;
    title: string;
    hideTitle?: boolean;
    maxNumberOfColumns: number;
    slideDetails: ProductCarouselSlideDetails;
}

const mapStateToProps = (state: ApplicationState) => ({
    productIds: getProductIdsForCompare(state),
    products: getProductsForCompare(state),
});

const mapDispatchToProps = {
    removeProductIdToCompare,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasShellContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface ProductCarouselSlideDetails {
    slides: CarouselSlide[];
    products: ProductModel[];
    attributeTypes: AttributeTypeModel[];
}

export interface CarouselSlide {
    id: string;
    renderComponent: (options: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions {}

export interface CarouselStyles {
    attributeLabelGridItem?: InjectableCss;
    attributeRow?: InjectableCss;
    attributeLableContainer: InjectableCss;
    attributeLabelText?: TypographyPresentationProps;
    attributeValueGridItem?: InjectableCss;
    attributeValueText?: TypographyPresentationProps;
    productCarouselStyle?: ProductCarouselProductStyles;
    clearIcon?: IconProps;
    arrowLeftIcon?: IconProps;
    arrowRightIcon?: IconProps;
}

export const carouselStyles: CarouselStyles = {
    attributeLabelGridItem: {
        css: css`
            border-bottom: 1px solid ${getColor("common.ui3")};
            &:first-child {
                border-top: 2px solid ${getColor("common.ui3")};
            }
        `,
    },
    attributeRow: {
        css: css`
            display: flex;
            align-items: baseline;
            @media (max-width: 1024px) {
                flex-direction: column;
            }
        `,
    },
    attributeLableContainer: {
        css: css`
            flex: 0 0 296px;
            padding-left: 20px;
            background-color: ${getColor("common.ui4")};
            @media (max-width: 1024px) {
                min-width: 100%;
                flex: unset;
            }
        `,
    },
    attributeLabelText: {
        ellipsis: true,
        css: css`
            font-weight: ${fontConfig.weight.bold};
            letter-spacing: 0;
            line-height: 40px;
            display: block;
        `,
    },
    attributeValueGridItem: {
        css: css``,
    },
    attributeValueText: {
        ellipsis: true,
        css: css`
            display: block;
            text-align: center;
        `,
    },
    productCarouselStyle: {
        customActionStyle: {
            css: css`
                position: absolute;
                top: 5px;
                right: 5px;
                z-index: 1;
            `,
        },
    },
    clearIcon: {
        src: "Cancel",
    },
    arrowLeftIcon: {
        src: "KeyboardArrowLeft",
    },
    arrowRightIcon: {
        src: "KeyboardArrowRight",
    },
};

export const carouselProductsContainer = css`
    ${styledSlider()}
    max-width: 1040px;
    margin-left: auto;
`;

export const carouselProductsAttributeContainer = css`
    ${styledSlider()}
    max-width: 1040px;

    .swiper-slide-active {
        ${xScreen("lte", "sm")} {
            width: 300px !important;
            justify-content: flex-start !important;
        }
    }
    .swiper-slide {
        min-width: 247px;
        ${xScreen("lte", "sm")} {
            display: flex !important;
            justify-content: center !important;
        }
        ${xScreen("lte", "xs")} {
            min-width: 100vw;
        }
    }
`;

const ProductCompareCarousel = ({
    shellContext,
    theme,
    id,
    extendedStyles,
    slideDetails,
    removeProductIdToCompare,
    maxNumberOfColumns,
    products,
}: Props) => {
    /**
     * Xng-Change
     * Change to Swiper slide instead of OOTB carousel
     * remove:
     *  getSlidesToScroll method
     *  slidesToScroll
     */
    React.useEffect(() => {
        // eslint-disable-next-line global-require
        require("swiper/swiper-bundle.min.css");
    }, []);

    const [styles] = useState(() => mergeToNew(carouselStyles, extendedStyles));

    const getProductAttributeValue = (product: ProductModel, attribute: AttributeTypeModel) => {
        const productAttributeValue = product.attributeTypes
            ?.filter(a => a.id === attribute.id)
            .map(a => a.attributeValues || [])
            .reduce((acc, currentValue) => [...acc, ...currentValue], []);

        if (productAttributeValue && productAttributeValue.length > 0) {
            return (
                <Typography
                    aria-describedby={`carousel-${id}_attribute-label-${attribute.id}`}
                    {...styles.attributeValueText}
                >
                    {productAttributeValue[0].valueDisplay}
                </Typography>
            );
        }

        return <>&nbsp;</>;
    };

    if (!slideDetails || slideDetails.slides.length === 0) {
        return null;
    }

    return (
        <StyledWrapper
            css={css`
                width: 100%;
                max-width: 100%;
                .swiper-button-prev {
                    left: -20px !important;
                    ${xScreen("lte", "sm")} {
                        left: 5px !important;
                    }
                }
                .failed-to-load {
                    max-width: unset !important;
                }

                .swiper-button-next {
                    right: -20px !important;
                    ${xScreen("lte", "sm")} {
                        right: 5px !important;
                    }
                }
            `}
        >
            {products?.length && (
                <StyledWrapper>
                    <StyledWrapper className="carousel-products-container" css={carouselProductsContainer}>
                        <Swiper
                            spaceBetween={20}
                            touchRatio={0}
                            navigation={{
                                nextEl: ".swiper-button-next",
                                prevEl: ".swiper-button-prev",
                            }}
                            className="swiper-carousel"
                            noSwipingClass="no-swiping-elements"
                            breakpoints={{
                                // when window width is >= 640px
                                0: {
                                    slidesPerView: 1,
                                },
                                // when window width is >= 768px
                                767: {
                                    slidesPerView: 2,
                                },
                                // when window width is >= 768px
                                1300: {
                                    slidesPerView: 3,
                                },
                                // when window width is >= 768px
                                1440: {
                                    slidesPerView: maxNumberOfColumns,
                                },
                            }}
                        >
                            {products?.map((product, index: any) => (
                                <SwiperSlide key={`carouselProduct${product.id}`}>
                                    <ProductCarouselProduct
                                        carouselId={id}
                                        product={product}
                                        showImage={true}
                                        showPartNumbers={true}
                                        showPrice={true}
                                        showAddToCart={true}
                                        showAddToList={true}
                                        extendedStyles={styles.productCarouselStyle}
                                        customActionsTemplate={(product: ProductModel) => {
                                            return (
                                                <Clickable
                                                    aria-label={translate("Remove Product from Compare")}
                                                    onClick={() => removeProductIdToCompare({ productId: product.id })}
                                                    data-test-selector="productCompareRemoveProductButton"
                                                    data-test-key={product.id}
                                                >
                                                    <VisuallyHidden>
                                                        {translate(
                                                            "Remove Product {0} from Compare",
                                                            product.productTitle,
                                                        )}
                                                    </VisuallyHidden>
                                                    <Icon size={18} {...styles.clearIcon} color="text.link" />
                                                </Clickable>
                                            );
                                        }}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        {products && products.length > 1 && (
                            <StyledWrapper className="swiper-navigation">
                                <div className="swiper-button-prev">
                                    <Icon size={20} color="secondary.main" {...styles.arrowLeftIcon} />
                                </div>
                                <div className="swiper-button-next">
                                    <Icon size={20} color="secondary.main" {...styles.arrowRightIcon} />
                                </div>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    <StyledWrapper
                        css={css`
                            margin-top: 49px;
                            @media (max-width: 1024px) {
                                overflow: hidden;
                            }
                        `}
                    >
                        {slideDetails.attributeTypes
                            .filter(att => att.isComparable)
                            .map(attribute => (
                                <StyledWrapper key={attribute.id} {...styles.attributeLabelGridItem}>
                                    <StyledWrapper {...styles.attributeRow}>
                                        <StyledWrapper {...styles.attributeLableContainer}>
                                            <Typography
                                                id={`carousel-${id}_attribute-label-${attribute.id}`}
                                                {...styles.attributeLabelText}
                                            >
                                                {attribute.label}
                                            </Typography>
                                        </StyledWrapper>
                                        <StyledWrapper
                                            className="carousel-products-container"
                                            css={carouselProductsAttributeContainer}
                                        >
                                            <Swiper
                                                spaceBetween={20}
                                                touchRatio={0}
                                                navigation={{
                                                    nextEl: ".swiper-button-next",
                                                    prevEl: ".swiper-button-prev",
                                                }}
                                                className="swiper-carousel "
                                                allowTouchMove={false}
                                                breakpoints={{
                                                    // when window width is >= 640px
                                                    0: {
                                                        slidesPerView: 1,
                                                    },
                                                    // when window width is >= 768px
                                                    767: {
                                                        slidesPerView: 2,
                                                    },
                                                    // when window width is >= 768px
                                                    1300: {
                                                        slidesPerView: 3,
                                                    },
                                                    // when window width is >= 768px
                                                    1440: {
                                                        slidesPerView: maxNumberOfColumns,
                                                    },
                                                }}
                                            >
                                                {products?.map((product, index: any) => (
                                                    <SwiperSlide key={`carouselProduct-attribute${product.id}`}>
                                                        <StyledWrapper
                                                            key={`${product.id}-attribute-${attribute.id}`}
                                                            {...styles.attributeValueGridItem}
                                                        >
                                                            {getProductAttributeValue(product, attribute)}
                                                        </StyledWrapper>
                                                    </SwiperSlide>
                                                ))}
                                            </Swiper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            ))}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

ProductCompareCarousel.defaultProps = {
    maxNumberOfColumns: 4,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(ProductCompareCarousel)));
